import React from 'react';
import { Link } from 'react-router-dom';
import HtmlContent from '../../../components/common/HtmlContent';
import useHashScroll from '../../../hooks/useHashScroll';

/**
 * OrganisationMappingPage Component
 * 
 * Displays organisation mapping tools content like the legacy HTML site
 */
const OrganisationMappingPage = () => {
  // Use the hash scroll hook to scroll to the correct section
  useHashScroll();
  
  // Organisation Mapping intro content from the legacy HTML
  const organisationMappingIntroHtml = `
    <p>
    As well as understanding the audience when developing a product, we need to understand the organisation and how the various stakeholders and parts fit together.  
    </p>

    <p>
    By getting a grasp, we ensure everyone is included and all areas considered, bringing everyone and everything along in the process - additionally, by leaving no stone unturned we can also discover further insights and opporunities. 
    </p>
  `;

  // Helper function to render tool info table with optional creator row
  const renderToolInfoTable = (category, updated, templates, creator = null) => {
    return (
      <div className="tool-table-wrap">
        <table id="tool-table">
          <tbody>
            <tr>
              <th colSpan="3">Tool Info</th>
            </tr>
            <tr>
              <td>Category</td>
              <td>{category}</td>  
            </tr>
            <tr>
              <td>Updated</td>
              <td>{updated}</td>
            </tr>
            <tr>
              <td>Templates</td>
              <td>{templates}</td>
            </tr>
            {creator && (
              <tr>
                <td>Creator</td>
                <td>{creator}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // Templates HTML for each tool
  const stakeholderMapTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/mapping/organisation-mapping/stakeholder-map.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_koJ8hoY=/" className="link-table">Miro</a>
    </>
  );

  const serviceBlueprintTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/mapping/organisation-mapping/service-blueprint.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_koRsj9s=/" className="link-table">Miro</a>
    </>
  );

  // Creator HTML
  const sakichiToyodaCreator = <a href="http://www.creativeeducationfoundation.org/facilitation_tool/5-ws-h/">Sakichi Toyoda</a>;
  const lynnShostacCreator = <a href="https://hbr.org/1984/01/designing-services-that-deliver">G. Lynn Shostac</a>;

  return (
    <>
      {/* Organisation Mapping Intro */}
      <div id="section-tools-organisation-mapping" className="tool-overview-container">
        <div className="tool-breadcrumb-container">
          <div className="tool-breadcrumb-wrap">
            <div className="tool-breadcrumb-icon">
              <i className="fas fa-bullhorn fa-2x"></i>
            </div>
            <div className="tool-breadcrumb-nav">
              <Link to="/research">Research Tools</Link>
              &nbsp;&nbsp;
              <i className="fas fa-angle-double-right"></i>
              &nbsp;&nbsp;
              <Link to="/research#mapping">Mapping</Link>
            </div>
            <div className="tool-breadcrumb-title">
              <Link to="/research/mapping/organisation-mapping">Organisation Mapping</Link>
            </div>
          </div>
        </div>
        <div className="tool-overview-para">
          <HtmlContent html={organisationMappingIntroHtml} containerClass="" />
        </div>
        <div className="tool-section-container">
          <div className="tool-section-wrap">
            <div className="tool-section-title">Section Tools</div>
          </div>
          <div className="tool-section-list">
            <div><Link className="tool-section-cta" to="#stakeholder-map">Stakeholder Map</Link></div>
            <div><Link className="tool-section-cta" to="#service-blueprint">Service Blueprint</Link></div>
          </div>
        </div>
      </div>

      {/* Stakeholder Map Section */}
      <main className="case-study">
        <span className="case-tool-title" id="stakeholder-map">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="stakeholder-map">
            <span className="tool-title-number">
              <Link to="/research/mapping/organisation-mapping#stakeholder-map">Stakeholder Map</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/research">Research</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/research#mapping">Mapping</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/research/mapping/organisation-mapping#stakeholder-map"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-stakeholder-map" 
            id="tab-stakeholder-map-overview" 
            defaultChecked
          />
          <label htmlFor="tab-stakeholder-map-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Organisation Mapping", "28/08/20", stakeholderMapTemplates, sakichiToyodaCreator)}
              <div className="tool-opening">
                <p>
                Connecting the dots of an organisation and understanding how people fit in to the matrix of decision making and influence is fraught with trickiness at the best of times, but it's even more difficult when developing new products.
                </p>
                
                <p>
                It is important to build and strengthen these connections, and whilst this sometimes can take a while, the Stakeholder Mapping tool can accelerate the process with a thorough visual understanding of how people fit together and creating a strategic way forward. 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://itk.mitre.org/stakeholder-map-matrix/" className="link-table">Stakeholder Map & Matrix</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://miro.com/blog/stakeholder-mapping/" className="link-table">Complete Stakeholder Mapping Guide</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-stakeholder-map" 
            id="tab-stakeholder-map-template"
          />
          <label htmlFor="tab-stakeholder-map-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_koJ8hoY=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Stakeholder Map Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Service Blueprint Section */}
      <main className="case-study">
        <span className="case-tool-title" id="service-blueprint">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="service-blueprint">
            <span className="tool-title-number">
              <Link to="/research/mapping/organisation-mapping#service-blueprint">Service Blueprint</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/research">Research</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/research#mapping">Mapping</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/research/mapping/organisation-mapping#service-blueprint"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-service-blueprint" 
            id="tab-service-blueprint-overview" 
            defaultChecked
          />
          <label htmlFor="tab-service-blueprint-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Organisation Mapping", "28/08/20", serviceBlueprintTemplates, lynnShostacCreator)}
              <div className="tool-opening">
                <p>
                Analysing the various touch points during a customer journey there are a number of components that come together through that journey. These service components are compromised of people, physical/digital props and processes can all be mapped using a Service Blueprint. 
                </p>
                
                <p>
                A good way to understand Service Blueprints is to consider them as Part 2 of a Customer Journey Map, a visualisation of an all encompassing omni channel experience - multiple touch points, and multiple departments and how they fit in to the Customer Journey.  
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/ux-mapping-cheat-sheet/" className="link-table">UX Mapping Methods Compared: A Cheat Sheet</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.interaction-design.org/literature/article/service-blueprints-communicating-the-design-of-services" className="link-table">Service Blueprints - Communicating the Design of Services</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-service-blueprint" 
            id="tab-service-blueprint-template"
          />
          <label htmlFor="tab-service-blueprint-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_koRsj9s=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Service Blueprint Template"
            ></iframe>
          </div>
        </div>
      </main>
    </>
  );
};

export default OrganisationMappingPage; 